

import { LeadResponsesDrawer } from './LeadResponsesDrawer';
import { TriageMemberPicker } from './TriageMemberPicker';

import { FALLBACK_VERIFY_SCHEMA } from './triageDeskShared';
import { useTriageController } from './useTriageController';
import type { ButtonHTMLAttributes } from 'react';
import { Modal, useModalClose } from '@/components/ui/Modal';
export function TriageVerificationDialogs({ ctx }: { ctx: ReturnType<typeof useTriageController> }) {
const { triageProfile, tab, rows, total, loading, toasts, verifyOpenFor, setVerifyOpenFor, verifyValues, setVerifyValues, verifySubmitting, irrelevantLead, setIrrelevantLead, irrelevantPendingId, irrelevantOriginQueryRef, irrelevantOriginDatasetRevisionRef, verifySchema, pickerFor, setPickerFor, pickerMode, members, membersLoading, dealerGroups, dealersLoading, assignBusy, responsesOpenFor, setResponsesOpenFor, pageSize, setPageSize, offset, setOffset, submitMemberAssign, submitIrrelevant, submitDealerAssign, createDealerFromPicker, optionsForField, submitVerify } = ctx;
return (<>{tab !== 'hot_overdue' && (
      <div className="flex items-center justify-between gap-3 px-1 py-3 text-sm text-gray-600">
        <div className="flex items-center gap-2">
          <span>Rows per page</span>
          <select
            value={pageSize}
            onChange={(e) => setPageSize(Number(e.target.value))}
            className="border border-gray-300 rounded px-2 py-1 bg-white focus:outline-none focus:ring-1 focus:ring-blue-500"
          >
            {[20, 40, 60, 80, 100].map((n) => (
              <option key={n} value={n}>{n}</option>
            ))}
          </select>
        </div>
        <div className="flex items-center gap-3">
          <span className="tabular-nums">
            {total === 0 ? '0' : `${offset + 1}–${Math.min(offset + pageSize, total)}`} of {total}
          </span>
          <button
            disabled={offset === 0 || loading}
            onClick={() => setOffset((o) => Math.max(0, o - pageSize))}
            className="px-3 py-1 rounded border border-gray-300 bg-white disabled:opacity-40 hover:border-gray-400"
          >Prev</button>
          <button
            disabled={offset + pageSize >= total || loading}
            onClick={() => setOffset((o) => o + pageSize)}
            className="px-3 py-1 rounded border border-gray-300 bg-white disabled:opacity-40 hover:border-gray-400"
          >Next</button>
        </div>
      </div>
      )}

      {/* Avika Responses Drawer */}
      <LeadResponsesDrawer
        leadId={responsesOpenFor?.id ?? null}
        onClose={() => setResponsesOpenFor(null)}
      />

      {/* L243 — irrelevant intake is archived only after an explicit confirm. */}
      {irrelevantLead && (
        <Modal
          open
          onClose={() => {
            if (!irrelevantPendingId) {
              irrelevantOriginQueryRef.current = null;
              irrelevantOriginDatasetRevisionRef.current = null;
              setIrrelevantLead(null);
            }
          }}
          ariaLabel="Mark lead as irrelevant"
          className="!max-w-md !rounded-lg p-5"
        >
            <h2 id="irrelevant-confirm-title" className="text-lg font-semibold text-gray-900">
              {`Mark “${irrelevantLead.name?.trim() || irrelevantLead.phone?.trim() || 'lead'}” as irrelevant?`}
            </h2>
            <div className="mt-5 flex justify-end gap-2">
              <RequestCloseButton
                disabled={irrelevantPendingId === irrelevantLead.id}
                className="px-3 py-1.5 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </RequestCloseButton>
              <button
                type="button"
                onClick={() => void submitIrrelevant()}
                disabled={irrelevantPendingId === irrelevantLead.id}
                className="px-3 py-1.5 text-sm bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50"
              >
                Mark irrelevant
              </button>
            </div>
        </Modal>
      )}

      {/* Manual Verify Modal */}
      {verifyOpenFor && (
        <Modal
          open
          onClose={() => { if (!verifySubmitting) setVerifyOpenFor(null); }}
          ariaLabel={verifyOpenFor.verifiedStatus === 'verified' || verifyOpenFor.verifiedBy ? 'Re-check details' : 'Verify lead manually'}
          className="!max-w-xl !rounded-lg p-5"
        >
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-lg font-semibold text-gray-900">{verifyOpenFor.verifiedStatus === 'verified' || verifyOpenFor.verifiedBy ? 'Re-check details' : 'Verify lead manually'}</h2>
              <RequestCloseButton
                disabled={verifySubmitting}
                className="text-gray-400 hover:text-gray-700 text-2xl leading-none disabled:opacity-50"
                aria-label="Close"
              >×</RequestCloseButton>
            </div>
            <div className="mb-3 text-sm">
              <div className="font-medium text-gray-900">{verifyOpenFor.name || '(no name)'}</div>
              <div className="font-mono text-xs text-gray-600">{verifyOpenFor.phone || '(no phone)'}</div>
              <div className="text-xs text-gray-500 mt-1">
                Same questions Avika asks on WhatsApp — call the customer and record the answers
                so this lead carries the same structured data as an Avika-qualified one.
              </div>
            </div>
            {/* Avika-parity form (L41 + qa4e): schema-driven, ordered; dropdowns
                for product (catalog) + state (gazetteer/local) + fixed sets;
                B2B sub-fields appear only when Application type = B2B. */}
            <div className="grid grid-cols-2 gap-3 max-h-[60vh] overflow-y-auto pr-1">
              {(verifySchema ?? FALLBACK_VERIFY_SCHEMA)
                .filter((f) => !f.showWhen || (verifyValues[f.showWhen.key] ?? '').trim() === f.showWhen.equals)
                .map((f) => {
                const val = verifyValues[f.key] ?? '';
                const set = (value: string) => setVerifyValues((prev) => ({ ...prev, [f.key]: value }));
                const opts = optionsForField(f);
                const wide = f.wide || f.type === 'textarea';
                return (
                  <label key={f.key} className={`block text-sm ${wide ? 'col-span-2' : ''}`}>
                    <span className="text-gray-700">{f.label}{f.required ? ' *' : ''}</span>
                    {f.type === 'textarea' ? (
                      <textarea
                        rows={2}
                        value={val}
                        onChange={(e) => set(e.target.value)}
                        placeholder={f.placeholder}
                        className="mt-1 w-full px-3 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                      />
                    ) : (f.type === 'select' || f.type === 'boolean') && (opts.length > 0 || f.type === 'boolean') ? (
                      <select
                        value={val}
                        onChange={(e) => set(e.target.value)}
                        className="mt-1 w-full px-3 py-1.5 text-sm border border-gray-300 rounded bg-white focus:outline-none focus:ring-1 focus:ring-blue-500"
                      >
                        <option value="">—</option>
                        {(f.type === 'boolean' ? ['Yes', 'No'] : opts).map((o) => (
                          <option key={o} value={o}>{o}</option>
                        ))}
                        {/* qa4e: keep a prefilled value selectable ONLY for
                            dynamic source-driven lists (product/state) — fixed
                            option sets must never surface out-of-enum legacy
                            values (e.g. a product string in a segment field). */}
                        {val && !!f.source && f.type !== 'boolean' && !opts.includes(val) && (
                          <option value={val}>{val}</option>
                        )}
                      </select>
                    ) : (
                      <input
                        type="text"
                        value={val}
                        onChange={(e) => set(e.target.value)}
                        placeholder={f.placeholder}
                        className="mt-1 w-full px-3 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                      />
                    )}
                  </label>
                );
              })}
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <RequestCloseButton
                disabled={verifySubmitting}
                className="px-3 py-1.5 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50"
              >Cancel</RequestCloseButton>
              <button
                onClick={submitVerify}
                disabled={verifySubmitting}
                className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
              >{verifySubmitting ? 'Verifying…' : 'Mark verified'}</button>
            </div>
        </Modal>
      )}

      {/* Toasts */}
      <div role="status" aria-live="polite" aria-atomic="false" className="fixed bottom-6 right-6 flex flex-col gap-2 z-50">
        {toasts.map((t) => (
          <div
            key={t.id}
            className="bg-gray-900 text-white text-sm rounded-lg shadow-lg px-4 py-3 flex items-center gap-3"
          >
            <span>{t.text}</span>
            {t.undo && (
              <button
                onClick={t.undo}
                className="text-blue-300 hover:text-blue-100 font-medium underline-offset-2 hover:underline"
              >
                Undo
              </button>
            )}
          </div>
        ))}
      </div>

      {/* L138 — searchable member picker (assign + L142 reassign-confirm). */}
      {pickerFor && (
        <TriageMemberPicker
          mode={pickerMode}
          leadName={pickerFor.name ?? pickerFor.phone ?? null}
          members={members}
          loading={membersLoading}
          busy={assignBusy}
          onClose={() => !assignBusy && setPickerFor(null)}
          onPick={(m) => void submitMemberAssign(m)}
          dealerGroups={dealerGroups}
          dealersLoading={dealersLoading}
          pickDealer={(d, g) => void submitDealerAssign(d, g)}
          createDealer={triageProfile ? undefined : createDealerFromPicker}
        />
      )}

      {/* CVG4-TRIAGE-1 (L117) — "+ New lead" modal */}
      </>);
}

function RequestCloseButton({ children, ...props }: ButtonHTMLAttributes<HTMLButtonElement>) {
  const { requestClose } = useModalClose();
  return <button type="button" {...props} onClick={requestClose}>{children}</button>;
}
